import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";

import { loadLocale } from "@smart/itops-locale-dom";
import { Alert, Modal } from "@smart/itops-sb-design-system-dom";
import {
  useLoadMatterTypeCategories,
  useQueryMatterTypes,
} from "@smart/manage-gql-client-dom";

import { RemappingFieldList } from "./remapping-field-list";
import { FormFieldsList } from "../../list-forms/creating/form-field-list";
import {
  EditingFormInfo,
  editingFormInfoSchema,
  formCategoryOptions,
} from "../../list-forms/form";
import {
  GqlField,
  GqlFormCategory,
  GqlGroup,
  GqlMatterType,
  GqlSetting,
} from "../../types";
import { useRemapFields } from "../hooks";
import { GqlSubmissionLinkHash } from "../types";

type EditFormInfoModalProps = {
  loading?: boolean;
  onClose: () => void;
  open: boolean;
  category?: GqlFormCategory;
  updateFormInfo: (values: EditingFormInfo) => Promise<void>;
  setting?: GqlSetting | null;
  matterTypeLocations: string[];
  matterTypes: GqlMatterType[];
  groups?: GqlGroup[] | null;
  fields?: GqlField[] | null;
  submissionLinkHashes?: GqlSubmissionLinkHash[];
};

const defaultMatterTypesGroups = (matterTypes: GqlMatterType[]) =>
  matterTypes.reduce(
    (result, matterType) => {
      const { name, category } = matterType;
      const existingName = result.find((item) => item.name === name);
      if (existingName) {
        existingName.matterTypes.push(matterType);
      } else {
        result.push({
          name,
          category,
          matterTypes: [matterType],
        });
      }
      return result;
    },
    [] as { name: string; category: string; matterTypes: GqlMatterType[] }[],
  );

export const EditFormInfoModal = ({
  loading,
  setting,
  onClose,
  updateFormInfo,
  open,
  category,
  matterTypeLocations,
  matterTypes,
  groups,
  fields,
  submissionLinkHashes,
}: EditFormInfoModalProps) => {
  const [isWebEnquiryWarningDismissed, setIsWebEnquiryWarningDismissed] =
    useState(false);

  useEffect(() => {
    if (open) setIsWebEnquiryWarningDismissed(false);
  }, [open]);
  const { terms, states } = loadLocale();
  const defaultLocations = [...new Set(matterTypeLocations)].sort();
  const { result: availableAreasOfLaw } = useLoadMatterTypeCategories({
    teamUri: setting?.teamUri,
  });

  const formSchema = editingFormInfoSchema({ terms });
  const {
    handleSubmit,
    control,
    watch,
    formState: { isSubmitting },
  } = useForm<EditingFormInfo>({
    defaultValues: {
      formCategory: category || "lead",
      locations: defaultLocations,
      matterTypeGroups: defaultMatterTypesGroups(matterTypes),
    },
    resolver: zodResolver(formSchema),
  });

  const [formCategory, locations, matterTypeGroups] = watch([
    "formCategory",
    "locations",
    "matterTypeGroups",
  ]);
  const {
    groupsToUpdate,
    fieldsToUpdate,
    hasSameMatterTypes,
    remapFields,
    isLoading: isCheckingRemapping,
  } = useRemapFields({
    groups,
    fields,
    currentMatterTypeIds: matterTypes.map((mt) => mt.id),
    targetMatterTypeIds: matterTypeGroups.flatMap((g) =>
      g.matterTypes.map((mt) => mt.id),
    ),
  });

  const areasOfLaw = setting?.areasOfLaw?.map((area) => area.trim());
  const matterTypeOptions = useQueryMatterTypes({
    type: formCategory,
    locations,
    categories:
      availableAreasOfLaw?.filter((areaOfLaw) =>
        areasOfLaw?.includes(areaOfLaw),
      ) || [],
  }).byCategory;

  const onSubmit = handleSubmit(async (values) => {
    await updateFormInfo(values);
    await remapFields();
    onClose();
  });

  return (
    <form onSubmit={onSubmit}>
      <Modal
        onClose={onClose}
        open={open}
        loading={loading || isCheckingRemapping || isSubmitting}
        header={{
          icon: "solidPencil",
          text: "Form Configuration",
          iconBgColor: "bg-cyan-140",
        }}
        footer={{
          buttons: [
            {
              text: "Cancel",
              variant: "secondarySubtle",
              onClick: onClose,
            },
            {
              text: "Update form",
              type: "submit",
              disabled: loading,
            },
          ],
        }}
      >
        <FormFieldsList
          control={control}
          formCategoryOptions={formCategoryOptions}
          locations={[
            ...new Set([
              ...(setting?.locations || states.map((s) => s.name)),
              ...defaultLocations,
            ]),
          ]}
          matterTypeOptions={matterTypeOptions.flatMap((c) =>
            c.loading
              ? [
                  {
                    category: c.category,
                    name: "Loading...",
                    disabled: true,
                    matterTypes: [],
                  },
                ]
              : c.matterTypes,
          )}
        />
        {category === "lead" &&
          formCategory === "matter" &&
          !!submissionLinkHashes?.length &&
          !isWebEnquiryWarningDismissed && (
            <Alert
              heading={`Changing the form type will break the web ${terms.enquiry.name} link`}
              content={`New submissions will stop, leads already captured aren't affected. Only Lead forms support web ${terms.enquiry.name} links.`}
              palette="warning"
              variant="info"
              onClose={() => setIsWebEnquiryWarningDismissed(true)}
            />
          )}
        {!hasSameMatterTypes && (
          <RemappingFieldList
            groups={groups}
            fields={fields}
            groupsToUpdate={groupsToUpdate}
            fieldsToUpdate={fieldsToUpdate}
          />
        )}
      </Modal>
    </form>
  );
};
